import React from 'react'
import { graphql } from 'gatsby'
import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'

import type { Paragraph, ParagraphComponent } from '../types'
import type { FieldLink } from '../../../drupal'
import type { MediaCroppedImage } from '../../Media'

import { media } from '../../../styles/variables'
import { classNames } from '../../ui/utils'
import Button from '../../ui/Button'
import Container from '../../Container'
import BackgroundImage from '../../BackgroundImage'
import { getImage, withArtDirection } from 'gatsby-plugin-image'

export interface HeroContentParagraph extends Paragraph {
	field_text: {
		processed: string
	}
	field_link: FieldLink
	field_animation: string
	relationships: {
		field_media: MediaCroppedImage
	}
}

/** TODO: fix animation */
function getAnimation(name: string) {
	switch (name) {
		case 'zoom':
			return keyframes`
				0% {
					transform:scale(1.1)
				}
				100% {
					transform:scale(1)
				}
			`
		default:
			return 'none'
	}
}

const HeroContent: ParagraphComponent<HeroContentParagraph> = ({ className, paragraph }) => {
	const images = withArtDirection(getImage(paragraph.relationships.field_media.fields.crop_landscape)!, [
		{
			media: "screen and (max-aspect-ratio: 1/1)",
			image: getImage(paragraph.relationships.field_media.fields.crop_portrait)!,
		},
	])

	return (
		<section
			className={classNames('hero', className)}
			id={`hero-${paragraph.drupal_internal__id}`}
		>
			<BackgroundImage image={images}>
				<Container>
					<div>
						<div className="body" dangerouslySetInnerHTML={{ __html: paragraph.field_text.processed }} />
						{paragraph.field_link && <Button link={paragraph.field_link} />}
					</div>
				</Container>
			</BackgroundImage>
		</section>
	)
}


export default styled(HeroContent)`
	color: white;

	${Container} {
		min-height: calc(100vh - 15px);
		padding-top: 60px;

		display: flex;
		align-items: center;
	}

	h1,
	h2,
	p {
		background: rgba(68, 0, 153, 0.6);
		line-height: 1.3;
	}

	h1 {
		font-size: 3.5rem;
	}

	h1,
	h2 {
		display: initial;
	}

	p {
		display: inline-block;
	}

	${media.up('sm')} {
		h1 {
			font-size: 5rem;
		}
	}

	${media.up('md')} {
		h1 {
			font-size: 7rem;
		}
	}
`

export const query = graphql`fragment HeroContentParagraph on paragraph__hero_content {
  drupal_internal__id
  field_text {
    processed
  }
  field_link {
    title
    link
    uri
  }
  field_animation
  relationships {
    field_media {
      fields {
        crop_portrait {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        crop_landscape {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
      }
    }
  }
}`
